import 'react';
import { itemHasBidsButReservePriceNotReached } from './item-labels';
import { columns } from '../../constants/column-definitions';
import { PAGE_NAMES } from '../../constants/page';
import {
    getBidCount,
    getMaxBid,
    getShippingCost
} from '../../helpers/item-helper';
import { infoItemComponents } from 'tradera-apps/my-tradera/app/components/listing/item/info-item-components';

const labels = {
    [columns.CurrentPrice]: 'Pris',
    [columns.TransactionDate]: 'Avslutad',
    [columns.EndDate]: 'Sluttid',
    [columns.ShippingCost]: 'Frakt',
    [columns.Bids]: '#Bud',
    [columns.ItemId]: 'Objektnr',
    [columns.OpeningBid]: 'Utrop',
    [columns.ReservePrice]: 'Res.pris',
    [columns.BuyItNow]: 'Köp nu',
    [columns.OwnReference]: 'Ref',
    [columns.MaxBid]: 'Maxbud',
    [columns.TimeLeft]: 'Tid kvar'
};

const columnCssClassCore = {
    [columns.CurrentPrice]: 'myt-list3__table-column-current-price', // show tablet
    [columns.TransactionDate]: 'myt-list3__table-column-end-date',
    [columns.EndDate]: 'myt-list3__table-column-end-date',
    [columns.ShippingCost]: 'myt-list3__table-column-shipping-cost', // show tablet
    [columns.Bids]: 'myt-list3__table-column-current-bids', // show tablet
    [columns.ItemId]: 'myt-list3__table-column-item-id',
    [columns.OpeningBid]: 'myt-list3__table-column-opening-bid',
    [columns.ReservePrice]: 'myt-list3__table-column-reserve-price',
    [columns.BuyItNow]: 'myt-list3__table-column-buy-it-now',
    [columns.OwnReference]: 'myt-list3__table-column-own-reference',
    [columns.MaxBid]: 'myt-list3__table-column-maxbid',
    [columns.TimeLeft]: 'myt-list3__table-column-timeleft'
};

const getCoreCssClass = column => columnCssClassCore[column];
const getHeading = (label, cssClass, columnKey) => ({
    term: label,
    cssClass: cssClass,
    columnKey
});

export const getTranslatedColumnKey = (t, columnKey) => {
    switch (columnKey) {
        case columns.CurrentPrice:
            return t('myt_columns_price');
        case columns.TransactionDate:
            return t('myt_columns_transactionDate');
        case columns.EndDate:
            return t('myt_columns_endTime');
        case columns.ShippingCost:
            return t('myt_columns_shipping');
        case columns.Bids:
            return `#${t('myt_columns_bids')}`;
        case columns.ItemId:
            return t('myt_columns_objectNo');
        case columns.OpeningBid:
            return t('myt_columns_startingPrice');
        case columns.ReservePrice:
            return t('myt_columns_reservePrice');
        case columns.BuyItNow:
            return t('myt_columns_buyNow');
        case columns.OwnReference:
            return t('myt_columns_ownReference');
        case columns.MaxBid:
            return t('myt_columns_maxBid');
        case columns.TimeLeft:
            return t('myt_columns_timeLeft');
        default:
            return '';
    }
};

export const getHeadings = (t, columnDefinitions) => {
    return columnDefinitions
        ? columnDefinitions.map(columnKey =>
              getHeading(
                  getTranslatedColumnKey(t, columnKey),
                  columnCssClassCore[columnKey]
              )
          )
        : [];
};

export const getUserHeading = (t, pageName) => {
    const highestBidder = t('myt_columns_highestBidder');

    switch (pageName) {
        case PAGE_NAMES.BuyerActiveItems:
            return getHeading(
                highestBidder,
                'myt-list3__table-column-counterpart'
            );
        case PAGE_NAMES.SellerActive:
            return getHeading(
                highestBidder,
                'myt-list3__table-column-counterpart'
            );
        case PAGE_NAMES.SellerUnsold:
            return getHeading(
                highestBidder,
                'myt-list3__table-column-counterpart'
            );
        case PAGE_NAMES.BuyerPurchases:
            return getHeading(
                'Mina Köp',
                'myt-list3__table-column-counterpart'
            );
        default:
            return null;
    }
};

/**
 * @name GetColumns
 * @desc Builds the columns out for a single item;
 */
export const getColumns = (columnDefinitions, listViewType) => item => {
    const getColumn = (label, value, cssClass, title, columnKey) => ({
        term: label,
        definition: value,
        cssClass: cssClass,
        tagTitle: title,
        columnKey
    });

    const hasTransactionDate =
        !!item &&
        item.transaction &&
        item.transaction &&
        item.transaction.transactionDate;
    const price = item.transaction
        ? parseInt(item.transaction.price)
        : parseInt(item.currentPrice);

    const getCurrentPriceMeta = () =>
        itemHasBidsButReservePriceNotReached(item)
            ? columnCssClassCore[columns.CurrentPrice] + ' text-danger bold'
            : columnCssClassCore[columns.CurrentPrice];

    const getItemId = item.itemId;
    const itemType = item.listing.itemType;
    const hasHighestBidder =
        item && item.bidInformation && item.bidInformation.leadingBidder
            ? true
            : false;
    const shouldShowBids =
        itemType === 'Auction' ||
        (hasHighestBidder && itemType === 'AuctionBin') ||
        (hasHighestBidder &&
            item.transaction &&
            item.listing.purchaseType === 'Auction')
            ? true
            : false;
    const bidCount = getBidCount(item);
    const getTransactionDate = hasTransactionDate
        ? item.transaction.transactionDate.formatted
        : null;
    const getEndDate = item.listing.endDate.formatted;
    const getCurrentPrice = () => ({
        component: infoItemComponents.ITEM_PRICE,
        transaction: item.transaction,
        price: price
    });
    const getTimeLeftAsString = item.listing.timeLeft
        ? item.listing.timeLeft.timeleft
        : null;

    const columnCssClass = {
        [columns.CurrentPrice]: getCurrentPriceMeta(),
        [columns.TransactionDate]:
            getCoreCssClass(columns.TransactionDate) +
            (hasTransactionDate && item.transaction.transactionDate.critical
                ? ' text-danger'
                : ''),
        [columns.EndDate]:
            getCoreCssClass(columns.EndDate) +
            (item.listing.endDate.critical ? ' text-danger' : ''),
        [columns.ShippingCost]: getCoreCssClass(columns.ShippingCost),
        [columns.Bids]: getCoreCssClass(columns.Bids),
        [columns.ItemId]: getCoreCssClass(columns.ItemId),
        [columns.OpeningBid]: getCoreCssClass(columns.OpeningBid),
        [columns.ReservePrice]: getCoreCssClass(columns.ReservePrice),
        [columns.BuyItNow]: getCoreCssClass(columns.BuyItNow),
        [columns.OwnReference]: getCoreCssClass(columns.OwnReference),
        [columns.Discount]: getCoreCssClass(columns.Discount) + ' text-danger',
        [columns.TimeLeft]: getCoreCssClass(columns.TimeLeft),
        [columns.MaxBid]: getCoreCssClass(columns.MaxBid)
    };

    const columnTitle = {
        [columns.CurrentPrice]: itemHasBidsButReservePriceNotReached(item)
            ? 'Reservationspris ej uppnått'
            : price,
        [columns.TransactionDate]: hasTransactionDate
            ? item.transaction.transactionDate.full
            : null,
        [columns.EndDate]:
            item &&
            item.listing &&
            item.listing.endDate &&
            item.listing.endDate.critical
                ? item.listing.timeLeft.timeleft
                : item.listing.endDate.full,
        [columns.ShippingCost]: '',
        [columns.Bids]: bidCount,
        [columns.ItemId]: '',
        [columns.OpeningBid]: '',
        [columns.ReservePrice]: '',
        [columns.BuyItNow]: '',
        [columns.OwnReference]: '',
        [columns.Discount]: '',
        [columns.TimeLeft]: getTimeLeftAsString
    };

    const getBids =
        shouldShowBids && bidCount
            ? {
                  component: infoItemComponents.BIDS,
                  itemId: getItemId,
                  bidCount: bidCount
              }
            : '-';
    const getOpeningBid =
        item.listing.pricing.openingBid > 0
            ? item.listing.pricing.openingBid
            : ' ';
    const getReservePrice =
        item.listing.pricing.reservePrice > 0
            ? item.listing.pricing.reservePrice
            : 0;
    const getBuyItNow =
        item.listing.pricing.buyItNow > 0 ? item.listing.pricing.buyItNow : 0;
    const getOwnReference = item.listing.ownReference
        ? item.listing.ownReference
        : ' ';
    const getDefinitionForPriceLabel = price => ({
        component: infoItemComponents.PRICE_LABEL,
        price
    });
    const getTimeLeft = {
        component: infoItemComponents.TIME_LEFT,
        timeLeft: item.listing.timeLeft
    };
    const shippingCost = getShippingCost(item, listViewType)();

    const definitions = {
        [columns.CurrentPrice]: getCurrentPrice(),
        [columns.TransactionDate]: getTransactionDate,
        [columns.EndDate]: getEndDate,
        [columns.ShippingCost]: shippingCost,
        [columns.Bids]: getBids,
        [columns.ItemId]: getItemId,
        [columns.OpeningBid]: getOpeningBid,
        [columns.ReservePrice]: getDefinitionForPriceLabel(getReservePrice),
        [columns.BuyItNow]: getDefinitionForPriceLabel(getBuyItNow),
        [columns.OwnReference]: getOwnReference,
        [columns.MaxBid]: getDefinitionForPriceLabel(getMaxBid(item)),
        [columns.TimeLeft]: getTimeLeft
    };

    return columnDefinitions.map(columnKey =>
        getColumn(
            columnKey === 'CurrentPrice' ? undefined : labels[columnKey],
            definitions[columnKey],
            columnCssClass[columnKey],
            columnTitle[columnKey],
            columnKey
        )
    );
};

export default {
    getColumns,
    getHeadings
};
